import { useState } from "react";
import ExternalLinkIcon from "../../../../assets/images/icons/external-link.svg";
import PointerClickIcon from "../../../../assets/images/icons/pointer-click.svg";
import TopCountryVisitChart from "./TopCountryVisitChart";
import { AnalyticsChart } from "./AnalyticsChart";
import { CollectedDataTable } from "../components/CollectedDataTable";
import { Demography } from "./Demography"
import { CardSkeleton, ChartSkeleton, RowSkeleton } from "../LinkSkeletons";
import { useSmartLinkAnalytics } from "../../../../queries/artistManger/smartLink.query";
import { STREAM_ICONS } from "../constants/allPlatformIcon";
import { STREAM_UI } from "../constants/allPlatformIcon";


const DEFAULT_STREAMS = [
    { name: "spotify", clicksToService: 0 },
    { name: "youtube", clicksToService: 0 }
];
const normalizeName = (name = "") =>
    name
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]/g, "_") // replaces - / space with _
        .replace(/_+/g, "_");

const getStreamIcon = (rawName) => {
    const key = normalizeName(rawName);

    return (
        STREAM_ICONS[key] ||
        STREAM_ICONS[key.replace("_music", "")] || // fallback e.g., amazon_music -> amazon
        ExternalLinkIcon
    );
};
export default function LinkAnalytics({ smartLinkId, type }) {
    // Bottom Tabs Section Design
    const [activeTab, setActiveTab] = useState('cities');
    const [selectedTab, setSelectedTab] = useState("contentAnalytics");
    const [analyticsRange, setAnalyticsRange] = useState("all-time");
    const tabsMenu = [
        { id: "contentAnalytics", label: "Content Analytics" },
        { id: "demographics", label: "Demographics" },
        { id: "collectedData", label: "Collected Data" },
    ];
    const [showPageViews, setShowPageViews] = useState(true);
    const [showServiceClicks, setShowServiceClicks] = useState(true);

    const {
        data: smartLinkAnalytics,
        isLoading: analyticsLoading,
    } = useSmartLinkAnalytics({
        smartLinkId,
        range: analyticsRange,
    });

    let finalStreams = [];
    // If API returned something, update final stream array
    if (smartLinkAnalytics?.streams && smartLinkAnalytics?.streams.length > 0) {
        const normalized = smartLinkAnalytics?.streams.map(s => ({
            ...s,
            name: normalizeName(s.name),
            clicksToService: s.clicksToService || 0
        }));
        finalStreams = [...normalized];
    } else {
        finalStreams = [...DEFAULT_STREAMS]
    }

    const handleRangeChange = (range) => {
        setAnalyticsRange(range);
    };


    return (
        <div className="">
            <div className="heading-wrapper mb-4">
                <div className="font-bold text-theme_clr1 text-lg">High-Level Stats</div>
                <div className="font-bold text-theme_clr1/70 text-base mb-4">See how your website is performing</div>
            </div>

            <div className="select-wrapper mb-5">
                <select
                    className="tw-form-control font-semibold !w-auto !pr-8"
                    value={analyticsRange}
                    onChange={(e) => handleRangeChange(e.target.value)}
                >
                    <option value="all-time">All Time</option>
                    <option value="weekly">Last 7 Days</option>
                    <option value="monthly">Last 30 Days</option>
                    <option value="yearly">Last 12 Months</option>
                </select>
            </div>


            {/* Stats Card Grid Section */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-4">
                {analyticsLoading ? (
                    <>
                        <CardSkeleton />
                        <CardSkeleton />
                        <CardSkeleton />
                    </>
                ) : (
                    <>
                        <div className="border border-theme_clr1 rounded-lg p-4">
                            <div className="icon-cover mb-2">
                                <span className="h-[40px] w-[40px] inline-flex items-center justify-center bg-theme_clr2 bg-opacity-40 rounded-lg p-1">
                                    <img src={PointerClickIcon} alt="Pointer Click Icon" />
                                </span>
                            </div>
                            <div className="font-bold text-sm text-theme_clr1 text-opacity-70">Page Views</div>
                            <div className="text-xl font-bold text-theme_clr1">
                                {smartLinkAnalytics?.totals?.clicks || 0}
                            </div>
                        </div>

                        <div className="border border-theme_clr1 rounded-lg p-4">
                            <div className="icon-cover mb-2">
                                <span className="h-[40px] w-[40px] inline-flex items-center justify-center bg-theme_clr2 bg-opacity-40 rounded-lg p-1">
                                    <img src={PointerClickIcon} alt="Pointer Click Icon" />
                                </span>
                            </div>
                            <div className="font-bold text-sm text-theme_clr1 text-opacity-70">Click to Service</div>
                            <div className="text-xl font-bold text-theme_clr1">
                                {smartLinkAnalytics?.totals?.clicksToService || 0}
                            </div>
                        </div>

                        <div className="border border-theme_clr1 rounded-lg p-4">
                            <div className="icon-cover mb-2">
                                <span className="h-[40px] w-[40px] inline-flex items-center justify-center bg-theme_clr2 bg-opacity-40 rounded-lg p-1">
                                    <img src={PointerClickIcon} alt="Pointer Click Icon" />
                                </span>
                            </div>
                            <div className="font-bold text-sm text-theme_clr1 text-opacity-70">Signups</div>
                            <div className="text-xl font-bold text-theme_clr1">
                                {smartLinkAnalytics?.totals?.audience?.length || 0}
                            </div>
                        </div>
                    </>
                )}
            </div>


            {/* Chart Section */}
            <div className="chart-wrapper mb-4">
                {analyticsLoading ? (
                    <ChartSkeleton />
                ) : (
                    <AnalyticsChart
                        timeline={smartLinkAnalytics?.timeline || []}
                        range={analyticsRange}
                        showPageViews={showPageViews}
                        showServiceClicks={showServiceClicks}
                    />
                )}
                <div className="flex justify-center items-center">
                    <div className="space-x-4">
                        <label className="inline-flex items-center">
                            <input
                                type="checkbox"
                                checked={showPageViews}
                                onChange={(e) => setShowPageViews(e.target.checked)}
                                className="form-checkbox text-theme_clr2"
                            />
                            <span className="font-bold ml-2 text-sm text-theme_clr1">
                                Page Views
                            </span>
                        </label>

                        <label className="inline-flex items-center">
                            <input
                                type="checkbox"
                                checked={showServiceClicks}
                                onChange={(e) => setShowServiceClicks(e.target.checked)}
                                className="form-checkbox text-theme_clr2"
                            />
                            <span className="font-bold ml-2 text-sm text-theme_clr2">
                                Clicks to service
                            </span>
                        </label>
                        {/* <label className="inline-flex items-center">
                            <input type="checkbox" className="form-checkbox text-theme_clr2" />
                            <span className="font-bold ml-2 text-sm text-[#F8A01D]">Social Link Clicks</span>
                        </label>
                        <label className="inline-flex items-center">
                            <input type="checkbox" className="form-checkbox text-theme_clr2" />
                            <span className="font-bold ml-2 text-sm text-[#8E0F8A]">Signups</span>
                        </label> */}
                    </div>
                </div>
            </div>

            <div className="bg-white border border-theme_clr1 rounded-lg p-4">
                <div className="tab-header border-b border-theme_clr2 dark:border-theme_clr2 mb-2 sticky top-0 z-10 bg-white">
                    <div className="flex-1 overflow-auto">
                        <ul className="flex flex-nowrap gap-4 text-sm font-jakarta font-bold text-left overflow-auto whitespace-nowrap">
                            {tabsMenu.map((tab) => (
                                <li key={tab.id} className="inline-block">
                                    <button onClick={() => setSelectedTab(tab.id)}
                                        className={`inline-block py-2 px-1 border-b-2 transition-all duration-200 text-nowrap ${selectedTab === tab.id
                                            ? "text-theme_clr1 border-theme_clr4"
                                            : "text-theme_clr1 text-opacity-70 border-transparent"
                                            }`}>
                                        {tab.label}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                <div className="font-jakarta tab-body content-wrapper overflow-auto flex-1 min-h-0 py-2">
                    {selectedTab === "contentAnalytics" && (
                        <div className="content-wrapper">
                            <div className="heading-wrapper font-bold mb-4">
                                <div className="heading text-lg text-theme_clr1">{type !== 'biolink' ? 'Streaming' : ''} Platforms</div>
                                <div className="subheading text-base text-theme_clr1/70">See how your website is performing by {type !== 'biolink' ? 'Streaming' : ''} platforms</div>
                            </div>

                            <div className="flex flex-col gap-3">
                                {analyticsLoading ? (
                                    Array.from({ length: 5 }).map((_, i) => <RowSkeleton key={i} />)
                                ) : (
                                    finalStreams.map((platform) => {
                                        const totalClicks = finalStreams.reduce(
                                            (sum, p) => sum + (p.clicksToService || 0),
                                            0
                                        );

                                        const percentage =
                                            totalClicks > 0
                                                ? Math.round((platform.clicksToService / totalClicks) * 100)
                                                : 0;

                                        const icon = getStreamIcon(platform.name);
                                        const ui = STREAM_UI[platform.name] || {
                                            label: platform.name,
                                            icon: null,
                                        };

                                        return (
                                            <div
                                                key={platform.name}
                                                className="item-wrapper flex items-center justify-between gap-5"
                                            >
                                                <div className="flex items-center gap-2">
                                                    {/* <img src={icon} alt="" className="w-[35px]" />
                                                    <span className="font-bold text-base text-theme_clr1 capitalize">
                                                        {platform.name.replace("_", " ")}
                                                    </span> */}
                                                    {ui.icon && (
                                                        <img
                                                            src={ui.icon}
                                                            alt={ui.label}
                                                            className="w-[35px]"
                                                        />
                                                    )}
                                                    <span
                                                        className="font-bold text-base text-theme_clr1 capitalize"
                                                    >
                                                        {ui.label}</span>
                                                </div>

                                                <div className="flex w-[calc(100%-170px)] gap-2 items-center">
                                                    <div className="font-bold text-theme_clr1">
                                                        {platform.clicksToService}
                                                    </div>
                                                    <div className="progressbar flex-1 h-[30px] bg-theme_clr2/30 rounded-lg overflow-hidden">
                                                        <div
                                                            className="h-full bg-theme_clr2 rounded-lg transition-all duration-500"
                                                            style={{ width: `${percentage}%` }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>

                        </div>
                    )}

                    {selectedTab === "demographics" && (
                        <div>
                            <Demography activeTab={activeTab} setActiveTab={setActiveTab} />
                            <TopCountryVisitChart location={smartLinkAnalytics?.locations || []} totalClicks={smartLinkAnalytics?.totals?.clicks} />
                        </div>
                    )}

                    {selectedTab === "collectedData" && (
                        <div>
                            <CollectedDataTable audience={smartLinkAnalytics?.audience || []} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
